import type { SeatKey } from './views';

/** Realtime Database layout (plan §2a). Everything under `core` is written only by Admin transactions. */
export const paths = {
  room: (roomId: string) => `rooms/${roomId}`,
  core: (roomId: string) => `rooms/${roomId}/core`,
  meta: (roomId: string) => `rooms/${roomId}/core/meta`,
  members: (roomId: string) => `rooms/${roomId}/core/members`,
  public: (roomId: string) => `rooms/${roomId}/core/public`,
  private: (roomId: string, key: SeatKey) => `rooms/${roomId}/core/private/${key}`,
  /** Top-level (not under rooms/) so one server listener can watch every room's presence. */
  presence: (roomId: string, uid: string) => `presence/${roomId}/${uid}`,
  roomPresence: (roomId: string) => `presence/${roomId}`,
  presenceRoot: () => 'presence',
  roomIndex: (roomId: string) => `roomIndex/${roomId}`,
  roomIndexRoot: () => 'roomIndex',
  /** Ended tables by end time — purged after the retention period (server-only). */
  endedRoom: (roomId: string) => `endedRooms/${roomId}`,
  endedRoomsRoot: () => 'endedRooms',
  code: (code: string) => `codes/${code}`,
  user: (uid: string) => `users/${uid}`,
  userProfile: (uid: string) => `users/${uid}/profile`,
  userStats: (uid: string) => `users/${uid}/stats`,
  userActiveRoom: (uid: string) => `users/${uid}/activeRoom`,
  /** Proof of the notice the player accepted and their analytics choice (DPDP s.6(10)). */
  userConsent: (uid: string) => `users/${uid}/consent`,
  fcmToken: (uid: string, tokenId: string) => `users/${uid}/fcmTokens/${tokenId}`,
  fcmTokens: (uid: string) => `users/${uid}/fcmTokens`,
  history: (uid: string) => `history/${uid}`,
  historyEntry: (uid: string, matchId: string) => `history/${uid}/${matchId}`,
  match: (matchId: string) => `matches/${matchId}`,
  /** Match archives by last update — purged after the retention period (server-only). */
  matchIndex: (matchId: string) => `matchIndex/${matchId}`,
  matchIndexRoot: () => 'matchIndex',
  rate: (uid: string) => `rate/${uid}`,
} as const;

/** Unambiguous 31-character alphabet for 6-char room codes (no 0/O/1/I/L). */
export const ROOM_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
export const ROOM_CODE_LENGTH = 6;

export function normalizeRoomCode(input: string): string {
  return input
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, ROOM_CODE_LENGTH);
}

export function isRoomCode(input: string): boolean {
  return (
    input.length === ROOM_CODE_LENGTH && [...input].every((ch) => ROOM_CODE_ALPHABET.includes(ch))
  );
}

/** Invite link for a room. Plain https on the app's own domain (Dynamic Links is shut down). */
export function inviteUrl(origin: string, code: string): string {
  return `${origin.replace(/\/$/, '')}/r/${code}`;
}
